// ============================================================================
// FSA Engagement Signature Panel Pane — FSA-2.3 Signature Workflow
// ============================================================================
// Per-engagement signature panel editor. Lives as a tab inside
// FsaEngagementScreen. Reads + writes via
// /api/v1/FsaEngagementSignaturePanels.
//
// Layout:
//   • Header — Audit Firm picker + Place + Report Date + Carry-Forward action
//   • Auditor side — partner list (add / remove rows, UDIN per row)
//   • Board side   — director list (add / remove rows, scoped to client +
//                    engagement date via the master picker's eligibleAsOf filter)
//   • Save button — POST /upsert with snapshots resolved server-side
//
// UDIN validation runs on save — 18-char ICAI format. Soft-validates per
// row so the auditor can fix without losing the row.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import {
  useFsaEngagementSignaturePanel,
  useFsaSignaturePanelCarryForward,
} from 'hooks/api/fsa/useFsaSignature';
import {
  fsaEngagementSignaturePanelService,
  fsaAuditFirmService,
  fsaAuditPartnerService,
  fsaDirectorSignatoryService,
} from 'api/services/fsa/fsaSignatureService';
import {
  UDIN_EXAMPLE,
  UDIN_FORMAT_MESSAGE,
  UDIN_REGEX,
  type UpsertEngagementSignaturePanelSignatoryItem,
  type SignatoryRole,
} from 'api/types/fsa/signature.types';

interface FsaSignaturePanelPaneProps {
  engagementId: string;
  /** Used as eligibleAsOf filter when picking directors (defaults to today). */
  clientId?: string | null;
  /**
   * Engagement template code. ICAI Non-Corporate Entity templates
   * (IN_ICAI_NCE_*) have no board — the governance side is the partners,
   * so the board-side UI relabels Director → Partner. Underlying role
   * value stays "DIRECTOR" (the board side); only the labels change.
   */
  templateCode?: string | null;
}

interface DraftSignatory {
  /** Local-only key so React can track rows without server IDs yet. */
  key: string;
  role: SignatoryRole;
  signatoryMasterId: string;
  /** Cached label so the dropdown still shows the picked option. */
  signatoryLabel: string;
  /** Cached snapshot fields shown read-only after pick (preview only). */
  designationPreview?: string;
  dinOrMembershipPreview?: string;
  /** For partners: per-engagement UDIN. */
  udinNumber: string;
  /** For ordering. */
  displayOrder: number;
}

const newDraft = (role: SignatoryRole, displayOrder: number): DraftSignatory => ({
  key: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  signatoryMasterId: '',
  signatoryLabel: '',
  udinNumber: '',
  displayOrder,
});

const toIsoDate = (s: string | null | undefined): string =>
  s ? new Date(s).toISOString().slice(0, 10) : '';

const FsaSignaturePanelPane: React.FC<FsaSignaturePanelPaneProps> = ({ engagementId, clientId, templateCode }) => {
  // ICAI Non-Corporate Entity templates → relabel the board side for the
  // owners. Role value stays DIRECTOR under the hood (the DB CHECK is
  // AUDITOR_PARTNER / DIRECTOR / OTHER); only the wording differs.
  //
  // The owner noun is per-template, not shared across all NCE templates: a
  // proprietary concern has one owner and no partners, so labelling its
  // signatory "Partner" is simply wrong. Matched on the specific suffix
  // rather than added to the generic NCE branch, so a future NCE template
  // (HUF, AOP) falls back to the neutral default until it declares its own.
  const upperTemplateCode = (templateCode ?? '').toUpperCase();
  const isNonCorporate = upperTemplateCode.startsWith('IN_ICAI_NCE');
  const isProprietorship = upperTemplateCode.startsWith('IN_ICAI_NCE_PROP');
  const boardNoun = isProprietorship ? 'Proprietor' : isNonCorporate ? 'Partner' : 'Director';
  const ownerSideHeading = isProprietorship
    ? 'Owner’s Side — Proprietor'
    : isNonCorporate
      ? 'Owners’ Side — Partners'
      : 'Board Side — Directors / Officers';
  const { data: panel, loading, refetch } = useFsaEngagementSignaturePanel(engagementId);
  const { load: loadCarryForward, loading: cfLoading } = useFsaSignaturePanelCarryForward();

  // ───── form state ─────
  const [auditFirmId, setAuditFirmId] = useState('');
  const [auditFirmLabel, setAuditFirmLabel] = useState('');
  const [place, setPlace] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [notes, setNotes] = useState('');
  const [auditors, setAuditors] = useState<DraftSignatory[]>([]);
  const [directors, setDirectors] = useState<DraftSignatory[]>([]);
  const [rowVersion, setRowVersion] = useState(0);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Hydrate form from saved panel (on first load + on refetch).
  useEffect(() => {
    if (!panel) {
      // No panel yet — leave defaults empty for first save.
      setAuditFirmId('');
      setAuditFirmLabel('');
      setPlace('');
      setReportDate('');
      setNotes('');
      setAuditors([]);
      setDirectors([]);
      setRowVersion(0);
      return;
    }
    setAuditFirmId(String(panel.auditFirmId));
    setAuditFirmLabel(
      panel.auditFirmName
        ? `${panel.auditFirmName}${panel.auditFirmRegistrationNumber ? ' (' + panel.auditFirmRegistrationNumber + ')' : ''}`
        : '',
    );
    setPlace(panel.place ?? '');
    setReportDate(toIsoDate(panel.reportDate));
    setNotes(panel.notes ?? '');
    setRowVersion(panel.rowVersion);

    const ap: DraftSignatory[] = [];
    const dir: DraftSignatory[] = [];
    panel.signatories.forEach((s, i) => {
      const draft: DraftSignatory = {
        key: `srv-${s.id}`,
        role: s.role,
        signatoryMasterId: String(s.signatoryMasterId ?? ''),
        signatoryLabel: `${s.nameSnapshot}${s.designationSnapshot ? ' · ' + s.designationSnapshot : ''}`,
        designationPreview: s.designationSnapshot,
        dinOrMembershipPreview: s.dinOrMembershipSnapshot ?? '',
        udinNumber: s.udinNumber ?? '',
        displayOrder: s.displayOrder || i + 1,
      };
      if (s.role === 'AUDITOR_PARTNER') ap.push(draft);
      else dir.push(draft);
    });
    setAuditors(ap.sort((a, b) => a.displayOrder - b.displayOrder));
    setDirectors(dir.sort((a, b) => a.displayOrder - b.displayOrder));
  }, [panel]);

  // ───── async option loaders ─────
  const loadAuditFirmOptions = useCallback(async (search: string) => {
    const resp = await fsaAuditFirmService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined, includeInactive: false });
    return resp.data.map(f => ({
      value: String(f.id),
      label: `${f.firmName} (${f.firmRegistrationNumber})`,
    }));
  }, []);

  const loadPartnerOptions = useCallback(async (search: string) => {
    if (!auditFirmId) return [];
    const resp = await fsaAuditPartnerService.getAll({
      page: 1, pageSize: 50,
      firmId: auditFirmId,
      searchTerm: search || undefined,
      includeInactive: false,
    });
    return resp.data.map(p => ({
      value: String(p.id),
      label: `${p.partnerName} (${p.membershipNumber}) · ${p.designation}`,
      // Cache extras for snapshot preview (read in onChange via second source-of-truth lookup)
      meta: {
        designation: p.designation,
        membership: p.membershipNumber,
      },
    }));
  }, [auditFirmId]);

  const loadDirectorOptions = useCallback(async (search: string) => {
    if (!clientId) return [];
    const eligibleAsOf = reportDate || toIsoDate(new Date().toISOString());
    const resp = await fsaDirectorSignatoryService.getAll({
      page: 1, pageSize: 50,
      clientId,
      eligibleAsOf,
      searchTerm: search || undefined,
      includeInactive: false,
    });
    return resp.data.map(d => ({
      value: String(d.id),
      label: `${d.signatoryName}${d.din ? ' (DIN: ' + d.din + ')' : ''} · ${d.designation}`,
      meta: {
        designation: d.designation,
        din: d.din ?? '',
      },
    }));
  }, [clientId, reportDate]);

  // ───── row editing ─────
  const updateAuditor = useCallback((key: string, patch: Partial<DraftSignatory>) => {
    setAuditors(prev => prev.map(s => s.key === key ? { ...s, ...patch } : s));
  }, []);
  const removeAuditor = useCallback((key: string) => {
    setAuditors(prev => prev.filter(s => s.key !== key));
  }, []);
  const addAuditor = useCallback(() => {
    setAuditors(prev => [...prev, newDraft('AUDITOR_PARTNER', prev.length + 1)]);
  }, []);

  const updateDirector = useCallback((key: string, patch: Partial<DraftSignatory>) => {
    setDirectors(prev => prev.map(s => s.key === key ? { ...s, ...patch } : s));
  }, []);
  const removeDirector = useCallback((key: string) => {
    setDirectors(prev => prev.filter(s => s.key !== key));
  }, []);
  const addDirector = useCallback(() => {
    setDirectors(prev => [...prev, newDraft('DIRECTOR', prev.length + 1)]);
  }, []);

  // ───── carry-forward ─────
  const [carryForwardConfirmOpen, setCarryForwardConfirmOpen] = useState(false);
  const handleCarryForwardClick = useCallback(() => {
    setCarryForwardConfirmOpen(true);
  }, []);
  const handleCarryForwardConfirm = useCallback(async () => {
    setCarryForwardConfirmOpen(false);
    const draft = await loadCarryForward(engagementId);
    if (!draft) {
      notificationService.toast.info('No prior-year panel available to carry forward.');
      return;
    }
    // Apply draft to form.
    setAuditFirmId(String(draft.auditFirmId));
    // Firm label will refresh on next save read; for now show ID.
    setAuditFirmLabel('');
    setPlace('');
    setReportDate('');
    setNotes('');
    const ap: DraftSignatory[] = [];
    const dir: DraftSignatory[] = [];
    (draft.signatories ?? []).forEach((s, i) => {
      const d: DraftSignatory = {
        key: `cf-${i}-${Math.random().toString(36).slice(2, 6)}`,
        role: s.role,
        signatoryMasterId: String(s.signatoryMasterId ?? ''),
        signatoryLabel: s.nameOverride ?? '',
        designationPreview: s.designationOverride ?? '',
        dinOrMembershipPreview: s.dinOrMembershipOverride ?? '',
        udinNumber: s.udinNumber ?? '',
        displayOrder: s.displayOrder || i + 1,
      };
      if (s.role === 'AUDITOR_PARTNER') ap.push(d);
      else dir.push(d);
    });
    setAuditors(ap);
    setDirectors(dir);
    notificationService.toast.success('Prior-year panel loaded. Review and Save.');
  }, [engagementId, loadCarryForward]);

  // ───── save ─────
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!auditFirmId) errors.auditFirmId = 'Audit firm is required';
    auditors.forEach((a, i) => {
      if (!a.signatoryMasterId) errors[`auditor-${a.key}`] = 'Partner is required';
      if (a.udinNumber && !UDIN_REGEX.test(a.udinNumber)) {
        errors[`udin-${a.key}`] = UDIN_FORMAT_MESSAGE;
      }
    });
    directors.forEach(d => {
      if (!d.signatoryMasterId) errors[`director-${d.key}`] = 'Signatory is required';
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [auditFirmId, auditors, directors]);

  const handleSaveClick = useCallback(() => {
    if (!validate()) return;
    setSaveConfirmOpen(true);
  }, [validate]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const signatories: UpsertEngagementSignaturePanelSignatoryItem[] = [
        ...auditors.map((a, i) => ({
          role: 'AUDITOR_PARTNER' as const,
          signatoryMasterId: a.signatoryMasterId,
          displayOrder: i + 1,
          udinNumber: a.udinNumber.trim() || null,
        })),
        ...directors.map((d, i) => ({
          role: 'DIRECTOR' as const,
          signatoryMasterId: d.signatoryMasterId,
          columnPosition: i < 4 ? i + 1 : null,
          displayOrder: i + 1,
        })),
      ];
      await fsaEngagementSignaturePanelService.upsert({
        primaEngagementId: engagementId,
        auditFirmId,
        place: place.trim() || null,
        reportDate: reportDate || null,
        notes: notes.trim() || null,
        signatories,
        rowVersion,
        statusCode: 'ACTIVE',
      });
      notificationService.toast.success('Signature panel saved.');
      setIsSaving(false);
      refetch();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Save failed', 6000);
    }
  }, [engagementId, auditFirmId, auditors, directors, place, reportDate, notes, rowVersion, refetch]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading signature panel…
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 1.25rem', overflow: 'auto', flex: 1 }}>
      {/* ───── Header card ───── */}
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', gap: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>Engagement Signature Panel</h3>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Picks the audit firm and signatories used on signed FS. Snapshots are taken at save time so master edits don't alter previously-signed engagements.
            </div>
          </div>
          <button
            className="btn btn-secondary"
            onClick={handleCarryForwardClick}
            disabled={cfLoading || isSaving}
            title="Load audit firm + signatories from the prior-year engagement"
            style={{ fontSize: '0.8125rem', padding: '0.5rem 0.875rem' }}
          >
            <IconSystem.refresh size={14} /> Carry Forward
          </button>
        </div>

        <div className="form-grid-3">
          <div className="form-group" data-field="auditFirmId">
            <SyutSelect
              label="Audit Firm"
              required
              value={auditFirmId}
              onChange={(val) => { setAuditFirmId((val as string) || ''); }}
              isAsync
              loadOptions={loadAuditFirmOptions}
              defaultOptions
              cacheOptions={false}
              seedOptions={auditFirmId && auditFirmLabel ? [{ value: auditFirmId, label: auditFirmLabel }] : undefined}
              error={fieldErrors.auditFirmId}
              placeholder="Select audit firm…"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Place</label>
            <input
              type="text"
              className="form-input"
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              maxLength={200}
              placeholder="e.g. Bengaluru"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Report Date</label>
            <input
              type="date"
              className="form-input"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
            />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Internal Notes</label>
          <textarea
            className="form-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Auditor remarks about the signing (not printed)."
          />
        </div>
      </div>

      {/* ───── Auditor side ───── */}
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h4 style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Auditor Side — Signing Partners</h4>
          <button className="btn btn-primary" onClick={addAuditor} disabled={!auditFirmId || isSaving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
            <IconSystem.add size={14} /> Add Partner
          </button>
        </div>
        {!auditFirmId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            Select an audit firm first to add partners.
          </div>
        )}
        {auditors.length === 0 && auditFirmId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            No partners added yet. Click <strong>Add Partner</strong> above.
          </div>
        )}
        {auditors.map((row, idx) => (
          <div key={row.key} style={{
            display: 'grid', gridTemplateColumns: '1fr 280px auto', gap: '0.5rem',
            alignItems: 'flex-end', padding: '0.5rem 0', borderTop: idx > 0 ? '1px solid var(--border-color)' : 'none',
          }}>
            <div className="form-group" style={{ marginBottom: 0 }} data-field={`auditor-${row.key}`}>
              <SyutSelect
                label={idx === 0 ? `Partner #${idx + 1}` : ''}
                value={row.signatoryMasterId}
                onChange={(val) => {
                  updateAuditor(row.key, {
                    signatoryMasterId: (val as string) || '',
                  });
                }}
                isAsync
                loadOptions={loadPartnerOptions}
                defaultOptions
                cacheOptions={false}
                seedOptions={row.signatoryMasterId && row.signatoryLabel
                  ? [{ value: row.signatoryMasterId, label: row.signatoryLabel }] : undefined}
                error={fieldErrors[`auditor-${row.key}`]}
                placeholder="Select partner…"
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }} data-field={`udin-${row.key}`}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>UDIN</label>
              <input
                type="text"
                className={`form-input${fieldErrors[`udin-${row.key}`] ? ' form-field-error' : ''}`}
                value={row.udinNumber}
                onChange={(e) => updateAuditor(row.key, { udinNumber: e.target.value.toUpperCase() })}
                placeholder={`e.g. ${UDIN_EXAMPLE}`}
                maxLength={18}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem', fontFamily: 'monospace' }}
              />
              {fieldErrors[`udin-${row.key}`] && (
                <div className="form-error-message" style={{ fontSize: '0.7rem' }}>{fieldErrors[`udin-${row.key}`]}</div>
              )}
            </div>
            <button
              onClick={() => removeAuditor(row.key)}
              title="Remove partner"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', padding: '0.375rem',
              }}
            >
              <IconSystem.trash size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* ───── Board side ───── */}
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h4 style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
            {ownerSideHeading}
          </h4>
          <button className="btn btn-primary" onClick={addDirector} disabled={!clientId || isSaving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
            <IconSystem.add size={14} /> Add {boardNoun}
          </button>
        </div>
        {!clientId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            Client not resolved yet.
          </div>
        )}
        {directors.length === 0 && clientId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            {isProprietorship ? 'No proprietor added yet.' : `No ${boardNoun.toLowerCase()}s added yet.`} Click <strong>Add {boardNoun}</strong> above. The picker filters to signatories active as of {reportDate || 'today'}.
          </div>
        )}
        {directors.map((row, idx) => (
          <div key={row.key} style={{
            display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem',
            alignItems: 'flex-end', padding: '0.5rem 0', borderTop: idx > 0 ? '1px solid var(--border-color)' : 'none',
          }}>
            <div className="form-group" style={{ marginBottom: 0 }} data-field={`director-${row.key}`}>
              <SyutSelect
                label={idx === 0 ? `Signatory #${idx + 1}` : ''}
                value={row.signatoryMasterId}
                onChange={(val) => {
                  updateDirector(row.key, {
                    signatoryMasterId: (val as string) || '',
                  });
                }}
                isAsync
                loadOptions={loadDirectorOptions}
                defaultOptions
                cacheOptions={false}
                seedOptions={row.signatoryMasterId && row.signatoryLabel
                  ? [{ value: row.signatoryMasterId, label: row.signatoryLabel }] : undefined}
                error={fieldErrors[`director-${row.key}`]}
                placeholder="Select signatory…"
                isClearable={false}
                size="sm"
              />
            </div>
            <button
              onClick={() => removeDirector(row.key)}
              title="Remove signatory"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', padding: '0.375rem',
              }}
            >
              <IconSystem.trash size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* ───── Save bar ───── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.5rem 0' }}>
        <button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save Signature Panel'}
        </button>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message="Save signature panel for this engagement? Master values for selected partners and signatories will be snapshotted onto the panel at save time."
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={isSaving}
        loadingMessage="Saving…"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={carryForwardConfirmOpen}
        title="Carry Forward from Prior Year?"
        message="Load audit firm + signatories from the prior-year engagement panel. Place and Report Date will be cleared (engagement-time facts you enter fresh). Existing unsaved changes in this form will be replaced."
        confirmLabel="Load"
        cancelLabel="Cancel"
        variant="default"
        loading={cfLoading}
        loadingMessage="Loading…"
        onConfirm={handleCarryForwardConfirm}
        onCancel={() => setCarryForwardConfirmOpen(false)}
      />
    </div>
  );
};

export default FsaSignaturePanelPane;
